import React, { useEffect, useRef, useState } from 'react';

import { styled } from '@linaria/react';
import { useTheme, ITheme } from 'src/theme';

import infoIcon from '@assets/icon/info.svg';
import errorIcon from '@assets/icon/error.svg';

export interface InformationBoxProps {
	variant?: 'default' | 'error';
	hasClose?: boolean;
	close?: boolean;
	outerHeight?: string;
	text?: any;
	/**
	 * For theme
	 */
	theme?: any;
	children?: React.ReactNode;
	margin: string | number;

}

interface StyledProps {
	variant?: 'default' | 'error';
	theme: ITheme,
	margin: string | number;
	hasClose?: boolean;
	close?: boolean;
	outerHeight?: string;
}

const OuterContainer = styled.div<StyledProps>`
  display: block;
  margin: ${props => (props.close) ? '0' : (typeof props.margin === 'number' ? `${props.margin}px` : props.margin)};
  padding: 0;
  width: 100%;
	height: ${props => (props.close) ? '0px' : props.outerHeight || 'auto'};
  position: relative;
	overflow: hidden;
	transition: height .18s linear, margin .18s linear;
`;

const Container = styled.div<StyledProps>`
	background-color: ${props => (props.variant === 'error') ? props.theme.colors.red[100] : props.theme.colors.grey[600]};
	border-radius: 5px;
  display: flex;
  margin: 0;
  padding: 0;
  width: 100%;
  position: absolute;
	top: 0;
	transform: ${props => (props.close) ? 'translate(0, -100%)' : 'translate(0, 0)'};
	transition: transform .18s linear;
`;

const HiddenContainer = styled.div<StyledProps>`
	background-color: ${props => props.theme.colors.blue[100]};
	border-radius: 5px;
	display: flex;
	height: ${props => (props.close) ? '0' : 'auto'};
	margin: 0;
	opacity: 0.2;
	padding: 0;
	width: 100%;
	position: relative;
	transition: height 5.18s linear;
`;

const IconContainer = styled.div<StyledProps>`
  display: inline-block;
  margin: 0;
  padding: 15px;
  width: 35px;
  position: relative;
`;

const IconElement = styled.img<StyledProps>`
  height: 35px;
  position: relative;
	user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
`;

const ContentContainer = styled.div<StyledProps>`
  display: block;
	font-family: ${props => props.theme.fontFamily};
  margin: 0;
  padding: 20px ${props => props.hasClose ? '45px' : '20px'} 20px 0;
  position: relative;
	text-align: left;
	& p {
		color: ${props => props.theme.colors.grey[500]};
		letter-spacing: -0.1px;
		line-height: 24px;
		margin: 0;
	}
`;

const Cross = styled.div<StyledProps>`
	color: ${props => props.theme.colors.grey[500]};
	cursor: pointer;
  display: inline-block;
  font-family: ${props => props.theme.fontFamily};
  font-size: 34px;
	line-height: 12px;
  transform: rotate(45deg);
  position: absolute;
  top: 15px;
  right: 15px;
	user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
`

const InformationBox = ({
	variant,
	hasClose,
	margin,
	children,
	...props
}: InformationBoxProps) => {
	const theme = useTheme();
	const htmlRef = useRef<HTMLDivElement>(null);
	const [close, setClose] = useState<boolean>(false);
	const [height, setHeight] = useState<string>('auto');

	const handleClose = (event: any): void => {
		setClose(true);
	}

	useEffect(() => {
		setTimeout(() => {
			if (htmlRef.current?.clientHeight) {
				setHeight(htmlRef.current?.clientHeight + 'px');
			}
		}, 600);
		const handleResize = () => {
			setHeight(htmlRef.current?.clientHeight + 'px');
		}
		window.addEventListener('resize', handleResize)

	}, [])

	return (
		<OuterContainer theme={theme} close={close} outerHeight={height} margin={margin}>
			<HiddenContainer
				ref={htmlRef}
				close={close}
				theme={theme}
				margin={margin}
			>
				<IconContainer theme={theme} margin={margin}>
					<IconElement theme={theme} margin={margin} src={(variant === 'error') ? errorIcon : infoIcon} />
				</IconContainer>
				<ContentContainer theme={theme} margin={margin} hasClose={hasClose}>
					{children}
				</ContentContainer>
			</HiddenContainer>

			<Container variant={variant} close={close} theme={theme} margin={margin}>
				<IconContainer theme={theme} margin={margin}>
					<IconElement theme={theme} margin={margin} src={(variant === 'error') ? errorIcon : infoIcon} />
				</IconContainer>
				<ContentContainer theme={theme} margin={margin} hasClose={hasClose}>
					{children}
				</ContentContainer>
				{
					(hasClose) ? (
						<Cross theme={theme} margin={margin} onClick={handleClose}>+</Cross>
					) : null
				}
			</Container>
		</OuterContainer>
	);
};

InformationBox.defaultProps = {
	hasClose: false,
	variant: "default",
	margin: "0 0 30px"
}

export { InformationBox }